import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Cpu,
  Plus,
  RefreshCw,
  Copy,
  CheckCircle2,
  Power,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Per-cafe automation panel:
 *  - Master toggle (platform-owner only)
 *  - List of boards (ESP32 devices)
 *  - "Add Board" wizard returning a pairing PIN
 *
 * Props:
 *   cafe     — the active cafe object (must include id, automation_enabled)
 *   api      — axios-like client (already auth-attached, base /api)
 *   onUpdate — called whenever the cafe row changes (e.g. toggle flipped)
 */
export default function CafeAutomationPanel({ cafe, api, onUpdate, onDevicesChange }) {
  const enabled = !!cafe?.automation_enabled;

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Add Board wizard
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    relay_count: 4,
    notes: "",
  });
  const [adding, setAdding] = useState(false);

  // PIN reveal modal (shown once after create / regenerate)
  const [pinModal, setPinModal] = useState(null); // {device_id, name, claim_pin, mqtt_username, mqtt_password?}

  const refresh = useCallback(async () => {
    if (!cafe?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/cafes/${cafe.id}/devices`);
      const list = data?.devices || [];
      setDevices(list);
      onDevicesChange?.(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [cafe?.id, api, onDevicesChange]);

  useEffect(() => {
    if (enabled) refresh();
    else { setDevices([]); onDevicesChange?.([]); }
  }, [enabled, refresh, onDevicesChange]);

  const toggleAutomation = async (next) => {
    if (toggling) return;
    setToggling(true);
    try {
      const { data } = await api.patch(
        `/platform/cafes/${cafe.id}/automation`,
        { enabled: next },
      );
      onUpdate?.(data);
      toast.success(
        next
          ? "Hardware automation enabled for this cafe."
          : "Hardware automation disabled. Existing wiring preserved.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update automation");
    } finally {
      setToggling(false);
    }
  };

  const createBoard = async () => {
    if (!addForm.name.trim()) {
      toast.error("Give the board a name");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(
        `/platform/cafes/${cafe.id}/devices`,
        {
          name: addForm.name.trim(),
          relay_count: Number(addForm.relay_count) || 4,
          notes: addForm.notes.trim() || null,
        },
      );
      setAddOpen(false);
      setAddForm({ name: "", relay_count: 4, notes: "" });
      setPinModal({
        device_id: data.id,
        name: data.name,
        claim_pin: data.claim_pin,
        mqtt_username: data.mqtt_username,
        mqtt_password: data.mqtt_password,
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create board");
    } finally {
      setAdding(false);
    }
  };

  const regenPin = async (deviceId, deviceName) => {
    try {
      const { data } = await api.post(
        `/platform/devices/${deviceId}/regenerate-pin`,
      );
      setPinModal({
        device_id: deviceId,
        name: deviceName,
        claim_pin: data.claim_pin,
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate PIN");
    }
  };

  const deleteDevice = async (d) => {
    if (
      !confirm(
        `Delete "${d.name}"? Any tables wired to its channels will be unlinked.`,
      )
    )
      return;
    try {
      await api.delete(`/platform/devices/${d.id}`);
      toast.success("Board deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const setStatus = async (d, status) => {
    try {
      await api.patch(`/platform/devices/${d.id}`, { status });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const copyText = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      toast.success(label);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-white/10">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Cpu className="w-4 h-4 text-amber-300" />
            <div className="text-sm font-black text-amber-200 uppercase tracking-widest">
              Hardware Automation
            </div>
          </div>
          <div className="text-[12px] text-gray-400 leading-relaxed">
            Smart table-lights via ESP32 boards. When OFF, every relay command
            is a no-op and the cafe owner sees no hardware options. Cafe
            wiring is preserved if you toggle it OFF later.
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <span
            className={`text-[10px] uppercase tracking-widest font-black ${
              enabled ? "text-emerald-300" : "text-gray-500"
            }`}
          >
            {enabled ? "ON" : "OFF"}
          </span>
          <Switch
            checked={enabled}
            disabled={toggling}
            onCheckedChange={toggleAutomation}
            data-testid="automation-toggle"
          />
        </div>
      </div>

      {enabled && (
        <div className="bg-black/40 border border-white/10 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-black">
              Boards ({devices.length})
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black uppercase tracking-widest text-[10px] font-black h-7"
              data-testid="add-board-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add Board
            </Button>
          </div>

          {loading && devices.length === 0 ? (
            <div className="text-gray-500 text-xs py-4 text-center">
              Loading…
            </div>
          ) : devices.length === 0 ? (
            <div className="text-gray-500 text-xs py-6 text-center">
              No boards yet. Click <b>Add Board</b> to provision the first one.
            </div>
          ) : (
            <div className="space-y-2">
              {devices.map((d) => (
                <DeviceRow
                  key={d.id}
                  d={d}
                  onRegenPin={() => regenPin(d.id, d.name)}
                  onDelete={() => deleteDevice(d)}
                  onToggleStatus={() =>
                    setStatus(d, d.status === "disabled" ? "active" : "disabled")
                  }
                  onCopy={copyText}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Board wizard */}
      <Dialog open={addOpen} onOpenChange={(v) => !v && setAddOpen(false)}>
        <DialogContent className="bg-[#121a16] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Cpu className="w-5 h-5 text-amber-300" />
              Add ESP32 Board
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              Creates a new board and shows a one-time pairing PIN that the
              ESP32 uses to claim its MQTT credentials.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                Board name
              </Label>
              <Input
                value={addForm.name}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Front Hall — Board 1"
                className="bg-black/40 border-white/10 text-white"
                data-testid="board-name-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                Relay channels
              </Label>
              <Select
                value={String(addForm.relay_count)}
                onValueChange={(v) =>
                  setAddForm((f) => ({ ...f, relay_count: Number(v) }))
                }
              >
                <SelectTrigger
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="relay-count-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121a16] border-white/10 text-white">
                  <SelectItem value="4">4 channels</SelectItem>
                  <SelectItem value="8">8 channels</SelectItem>
                  <SelectItem value="16">16 channels</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] text-gray-500 mt-1">
                Pick the relay module wired to this ESP32.
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">
                Internal note (optional)
              </Label>
              <Input
                value={addForm.notes}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, notes: e.target.value }))
                }
                placeholder="Installed near billing counter…"
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-gray-300 bg-transparent hover:bg-white/5 flex-1 uppercase tracking-widest text-xs font-black"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={createBoard}
              disabled={adding}
              className="bg-amber-500 hover:bg-amber-600 text-black flex-1 uppercase tracking-widest text-xs font-black"
              data-testid="create-board-btn"
            >
              {adding ? "Creating…" : "Create board"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* One-time PIN modal */}
      <Dialog open={!!pinModal} onOpenChange={(v) => !v && setPinModal(null)}>
        <DialogContent className="bg-[#121a16] border border-amber-400/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-300" />
              Pairing PIN
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              Show this to the technician installing <b>{pinModal?.name}</b>.
              The PIN expires in 30 minutes and is shown only once.
            </DialogDescription>
          </DialogHeader>

          {pinModal && (
            <div className="space-y-3 mt-2">
              <div
                className="bg-black/60 border border-amber-400/30 rounded-lg p-5 text-center"
                data-testid="pin-display"
              >
                <div className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-1">
                  6-digit Pairing PIN
                </div>
                <div className="text-4xl font-black tracking-[0.4em] text-amber-200 font-mono">
                  {pinModal.claim_pin}
                </div>
                <button
                  type="button"
                  onClick={() => copyText(pinModal.claim_pin, "PIN copied")}
                  className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-amber-200 mt-2 inline-flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>

              {pinModal.mqtt_username && (
                <div className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">
                    MQTT credentials (for reference)
                  </div>
                  <div className="font-mono text-gray-300">
                    user: <span className="text-white">{pinModal.mqtt_username}</span>
                  </div>
                  {pinModal.mqtt_password && (
                    <div className="font-mono text-gray-300 break-all">
                      pass: <span className="text-white">{pinModal.mqtt_password}</span>
                      <button
                        type="button"
                        onClick={() =>
                          copyText(pinModal.mqtt_password, "Password copied")
                        }
                        className="ml-2 text-amber-300 hover:text-amber-200 align-middle"
                      >
                        <Copy className="w-3 h-3 inline" />
                      </button>
                    </div>
                  )}
                  <div className="text-[10px] text-gray-500 mt-1">
                    The ESP32 receives these automatically when it claims the
                    PIN — you usually don&apos;t need to copy them.
                  </div>
                </div>
              )}

              <Button
                type="button"
                onClick={() => setPinModal(null)}
                className="bg-amber-500 hover:bg-amber-600 text-black w-full uppercase tracking-widest text-xs font-black"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeviceRow({ d, onRegenPin, onDelete, onToggleStatus, onCopy }) {
  const provisioning = d.status === "provisioning";
  const disabled = d.status === "disabled";
  return (
    <div
      className="bg-black/30 border border-white/10 rounded-md p-3 flex items-center gap-3"
      data-testid={`device-row-${d.id}`}
    >
      <div className="shrink-0">
        <Cpu
          className={`w-5 h-5 ${
            disabled
              ? "text-gray-600"
              : provisioning
                ? "text-amber-300"
                : "text-emerald-300"
          }`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-white text-sm truncate">{d.name}</div>
          <span
            className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded ${
              provisioning
                ? "bg-amber-400/20 text-amber-300"
                : disabled
                  ? "bg-gray-500/20 text-gray-400"
                  : "bg-emerald-400/20 text-emerald-300"
            }`}
          >
            {d.status}
          </span>
          <span className="text-[10px] text-gray-500 font-mono">
            {d.relay_count}ch
          </span>
        </div>
        <div className="text-[10px] text-gray-500 font-mono mt-0.5 truncate">
          {d.id.slice(0, 8)} · {d.mac || "no mac yet"}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1">
        <button
          type="button"
          onClick={onRegenPin}
          title="Generate new pairing PIN"
          className="p-1.5 rounded text-gray-400 hover:text-amber-200 hover:bg-white/5"
          data-testid={`regen-pin-${d.id}`}
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onToggleStatus}
          title={disabled ? "Re-enable" : "Disable (stop sending commands)"}
          className={`p-1.5 rounded hover:bg-white/5 ${
            disabled
              ? "text-gray-500 hover:text-emerald-300"
              : "text-emerald-300 hover:text-amber-200"
          }`}
        >
          <Power className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete board"
          className="p-1.5 rounded text-gray-500 hover:text-rose-300 hover:bg-white/5"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
